
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, Form, Button, Alert, Row, Col, InputGroup } from 'react-bootstrap';
import axios from 'axios';
import { Navigate } from 'react-router-dom';

function TwoFactorSetup() {
    const { register, handleSubmit, formState: { errors }, watch } = useForm();
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isCompleted, setIsCompleted] = useState(false);
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [secret, setSecret] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [step, setStep] = useState(1); // 1: Generate QR, 2: Verify code, 3: Backup codes
    const [backupCodes, setBackupCodes] = useState([]);
    
    const verificationCode = watch('verificationCode');

    useEffect(() => {
        generateQRCode();
    }, []);

    const generateQRCode = async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setError('Authentication token not found. Please login again.');
            return;
        }

        try {
            const response = await axios.post('/auth/2fa/setup', {}, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            setQrCodeUrl(response.data.qrCodeUrl);
            setSecret(response.data.secret);
            // Store backup codes if provided in setup response
            if (response.data.backupCodes) {
                // Convert comma-separated string to array
                const codesArray = response.data.backupCodes.split(',').map(code => code.trim());
                setBackupCodes(codesArray);
            }
            setIsLoading(false);
        } catch (err) {
            console.error('QR Generation error:', err);
            if (err.response?.status === 401) {
                setError('Session expired. Please login again.');
            } else {
                setError('Failed to generate QR code. Please try again.');
            }
            setIsLoading(false);
        }
    };

    const verifyCode = async (data) => {
        const token = localStorage.getItem('token');
        if (!token) {
            setError('Authentication token not found. Please login again.');
            return;
        }

        try {
            const bodyFormData = new FormData();
            bodyFormData.append("code", data.verificationCode);

            const response = await axios.post('/auth/2fa/verify', bodyFormData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'content-type': 'application/x-www-form-urlencoded'
                }
            });

            if (response.data.valid && response.data.enabled) {
                setStep(3);
                setSuccess('Two-factor authentication has been successfully enabled!');
                localStorage.removeItem('requires2FA');
            } else {
                setError('Invalid verification code. Please try again.');
            }
        } catch (err) {
            console.error('Verification error:', err);
            if (err.response?.status === 401) {
                setError('Session expired. Please login again.');
            } else {
                setError('Invalid verification code. Please try again.');
            }
        }
    };

    const finishSetup = () => {
        localStorage.removeItem('requires2FA');
        setIsCompleted(true);
    };

    const downloadBackupCodes = () => {
        const codesText = backupCodes.map((code, index) => `${index + 1}. ${code}`).join('\n');
        const blob = new Blob([
            `Panoptes 2FA Backup Codes\n` +
            `Generated: ${new Date().toLocaleString()}\n` +
            `Account: ${localStorage.getItem('id')}\n\n` +
            `IMPORTANT: Keep these codes safe and secure!\n` +
            `Each code can only be used once.\n\n` +
            `Backup Codes:\n${codesText}`
        ], { type: 'text/plain' });
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `panoptes-2fa-backup-codes-${localStorage.getItem('id')}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    if (isCompleted) {
        const userRole = localStorage.getItem('role');
        const userId = localStorage.getItem('id');
        
        if (userRole?.includes('TEACHER')) {
            return <Navigate to="/" />;
        } else if (userRole === 'STUDENT') {
            return <Navigate to={`/student/${userId}`} />;
        } else {
            return <Navigate to="/login" />;
        }
    }

    return (
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '20px' }}>
            <Card>
                <Card.Body>
                    <Card.Title className="text-center mb-4">
                        Set Up Two-Factor Authentication
                    </Card.Title>
                    
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className={`badge ${step >= 1 ? 'bg-primary' : 'bg-secondary'}`}>
                                1. Scan QR Code
                            </div>
                            <div className={`badge ${step >= 2 ? 'bg-primary' : 'bg-secondary'}`}>
                                2. Verify Code
                            </div>
                            <div className={`badge ${step >= 3 ? 'bg-primary' : 'bg-secondary'}`}>
                                3. Save Backup Codes
                            </div>
                        </div>
                        <div className="progress mt-2" style={{ height: '3px' }}>
                            <div 
                                className="progress-bar" 
                                style={{ width: `${(step / 3) * 100}%` }}
                            ></div>
                        </div>
                    </div>

                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}

                    {/* Step 1: QR Code */}
                    {step === 1 && (
                        <>
                            <Alert variant="info">
                                <Alert.Heading>Step 1: Set up your authenticator app</Alert.Heading>
                                <p>
                                    Download and install an authenticator app like Google Authenticator, 
                                    Microsoft Authenticator, or Authy on your mobile device.
                                </p>
                            </Alert>

                            {isLoading ? (
                                <div className="text-center py-4">
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-2">Generating QR code...</p>
                                </div>
                            ) : (
                                <Row>
                                    <Col md={6} className="text-center">
                                        <h5>Scan this QR code:</h5>
                                        {qrCodeUrl && (
                                            <img 
                                                src={qrCodeUrl} 
                                                alt="2FA QR Code" 
                                                className="img-fluid border rounded p-2 mb-3"
                                                style={{ maxWidth: '250px' }}
                                            />
                                        )}
                                    </Col>
                                    <Col md={6}>
                                        <h5>Or enter this code manually:</h5>
                                        <div className="alert alert-light">
                                            <code style={{ fontSize: '0.9em', wordBreak: 'break-all' }}>
                                                {secret}
                                            </code>
                                        </div>
                                        <small className="text-muted">
                                            Copy this code into your authenticator app if you can't scan the QR code.
                                        </small>
                                    </Col>
                                </Row>
                            )}

                            <div className="d-grid mt-4">
                                <Button 
                                    variant="primary" 
                                    onClick={() => setStep(2)}
                                    disabled={isLoading}
                                >
                                    I've Added the Account to My App
                                </Button>
                            </div>
                        </>
                    )}

                    {/* Step 2: Verify Code */}
                    {step === 2 && (
                        <>
                            <Alert variant="info">
                                <Alert.Heading>Step 2: Verify your setup</Alert.Heading>
                                <p>
                                    Enter the 6-digit code from your authenticator app to verify that 
                                    two-factor authentication is working correctly.
                                </p>
                            </Alert>

                            <Form onSubmit={handleSubmit(verifyCode)}>
                                <Form.Group className="mb-3" controlId="formVerificationCode">
                                    <Form.Label>Verification Code</Form.Label>
                                    <Form.Control 
                                        {...register("verificationCode", { 
                                            required: "Verification code is required",
                                            pattern: {
                                                value: /^[0-9]{6}$/,
                                                message: "Please enter a 6-digit code"
                                            }
                                        })} 
                                        type="text" 
                                        placeholder="000000"
                                        maxLength="6"
                                        className="text-center"
                                        style={{ fontSize: '1.5em', letterSpacing: '0.3em' }}
                                    />
                                    <Form.Text className="text-muted">
                                        Enter the 6-digit code from your authenticator app
                                    </Form.Text>
                                    {errors.verificationCode && (
                                        <div className="text-danger mt-1">{errors.verificationCode.message}</div>
                                    )}
                                </Form.Group>

                                <Row>
                                    <Col>
                                        <Button 
                                            variant="outline-secondary" 
                                            onClick={() => setStep(1)}
                                            className="w-100"
                                        >
                                            Back
                                        </Button>
                                    </Col>
                                    <Col>
                                        <Button 
                                            variant="primary" 
                                            type="submit"
                                            className="w-100"
                                            disabled={!verificationCode || verificationCode.length !== 6}
                                        >
                                            Verify Code
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </>
                    )}

                    {/* Step 3: Backup Codes */}
                    {step === 3 && (
                        <>
                            <Alert variant="warning">
                                <Alert.Heading>Step 3: Save your backup codes</Alert.Heading>
                                <p>
                                    <strong>Important:</strong> Save these backup codes in a secure location. 
                                    You can use them to access your account if you lose access to your 
                                    authenticator app. Each code can only be used once.
                                </p>
                            </Alert>

                            {backupCodes.length > 0 && (
                                <div className="mb-4">
                                    <h5>Your Backup Codes:</h5>
                                    <div className="alert alert-light">
                                        <Row>
                                            {backupCodes.map((code, index) => (
                                                <Col key={index} md={6} className="mb-2">
                                                    <code className="d-block p-2 bg-white border rounded">
                                                        {index + 1}. {code}
                                                    </code>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                    
                                    <div className="d-grid gap-2 d-md-flex justify-content-md-center mb-3">
                                        <Button 
                                            variant="outline-primary" 
                                            onClick={downloadBackupCodes}
                                        >
                                            📥 Download Backup Codes
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <Alert variant="success">
                                <p className="mb-0">
                                    ✅ Two-factor authentication is now enabled for your account!
                                </p>
                            </Alert>

                            <div className="d-grid">
                                <Button 
                                    variant="primary" 
                                    size="lg"
                                    onClick={finishSetup}
                                >
                                    Complete Setup
                                </Button>
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}

export default TwoFactorSetup;